import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useForm, Controller, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormInputText from '../../components/form/FormInputText';
import FormDropdown from '../../components/form/FormDropdown';
import FormInputNumber from '../../components/form/FormInputNumber';
import FormCalendar from '../../components/form/FormCalendar';
import {
  FeeHudLineViewModel,
  CreateOrUpdateFeeHudLineViewModel,
  PaidTo,
  IntegrationVendor,
  FeeToleranceLevel,
  FeeRestrictionsViewModel,
  FeeLosMetadataConfigurationViewModel,
} from '../../models/fees';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { getFieldMetadata } from '../../services/expressionService';
import { MetadataContextType, FieldMetadata, DataType } from '../../models/expression';
import * as commonService from '../../services/commonService';
import { EnumValueData } from '../../models/enum';

interface HudLineEditorDialogProps {
  visible: boolean;
  hudLine: FeeHudLineViewModel | null;
  onSave: (payload: CreateOrUpdateFeeHudLineViewModel) => Promise<void>;
  onCancel: () => void;
}

const schema = yup.object().shape({
  hudLine: yup.string().required('HUD Line is required'),
  description: yup.string().required('Description is required'),
  hudSection: yup.string().required('HUD Section is required'),
  loanEstimateSection: yup.string().required('Loan Estimate Section is required'),
  closingDisclosureSection: yup.string().required('Closing Disclosure Section is required'),
  group: yup.string().nullable(),
  groupDetails: yup.string().nullable(),
  isAprAffiliate: yup.boolean().required(),
  isAprNonAffiliate: yup.boolean().required(),
  estimateIncludedInEscrow: yup.boolean().required(),
  borrowerCanShopFor: yup.boolean().required(),
  paidTo: yup.mixed<PaidTo>().required(),
  losVendor: yup.mixed<IntegrationVendor>().required(),
  restrictions: yup.mixed<FeeRestrictionsViewModel>().required(),
  losMetadata: yup.mixed<FeeLosMetadataConfigurationViewModel[]>().required(),
});

const HudLineEditorDialog: React.FC<HudLineEditorDialogProps> = ({ visible, hudLine, onSave, onCancel }) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateOrUpdateFeeHudLineViewModel>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      hudLine: '',
      description: '',
      hudSection: '',
      loanEstimateSection: '',
      closingDisclosureSection: '',
      group: '',
      groupDetails: '',
      isAprAffiliate: false,
      isAprNonAffiliate: false,
      estimateIncludedInEscrow: false,
      borrowerCanShopFor: false,
      paidTo: PaidTo.None,
      losVendor: IntegrationVendor.None,
      restrictions: {
        toleranceLevel: FeeToleranceLevel.None,
        canEditBorrowerAmount: false,
        canEditSellerAmount: false,
        canEditBrokerAmount: false,
        canEditLenderAmount: false,
        canEditIncrementDollarAmount: false,
        canEditOtherAmount: false,
        canClientIgnore: false,
      },
      losMetadata: [],
    },
  });

  const losMetadata: FeeLosMetadataConfigurationViewModel[] = useWatch({
    control,
    name: 'losMetadata',
  }) || [];

  const [losFieldMetadata, setLosFieldMetadata] = useState<FieldMetadata[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [enumValuesCache, setEnumValuesCache] = useState<Record<string, EnumValueData[]>>({});
  const [loadingEnums, setLoadingEnums] = useState<Record<string, boolean>>({});
  const fetchingEnumsRef = useRef<Set<string>>(new Set());
  const fetchingFieldMetadataRef = useRef(false);

  useEffect(() => {
    if (!visible) return;

    if (losFieldMetadata.length > 0 || fetchingFieldMetadataRef.current) return;

    const fetchLosFields = async () => {
      fetchingFieldMetadataRef.current = true;
      try {
        setLoadingFields(true);
        const fields = await getFieldMetadata(MetadataContextType.LosMetadataFeeValue);
        setLosFieldMetadata(fields);
      } catch (error) {
        console.error('Failed to fetch LOS field metadata:', error);
        setLosFieldMetadata([]);
      } finally {
        setLoadingFields(false);
        fetchingFieldMetadataRef.current = false;
      }
    };

    fetchLosFields();
  }, [visible, losFieldMetadata.length]);

  const getLosFieldMetadata = (item: FeeLosMetadataConfigurationViewModel): FieldMetadata | null => {
    const searchValue = item.fieldName || item.losFieldId;
    if (!searchValue) return null;
    return losFieldMetadata.find(f => f.fieldName === searchValue) || null;
  };

  const handleAddLosMetadata = () => {
    const currentData = losMetadata || [];
    const newData = [...currentData, { losFieldId: '', fieldName: null, value: '' }];
    setValue('losMetadata', newData, { shouldValidate: true });
  };

  const handleRemoveLosMetadata = (index: number) => {
    const currentData = losMetadata || [];
    const newData = currentData.filter((_, i) => i !== index);
    setValue('losMetadata', newData, { shouldValidate: true });
  };

  const handleLosFieldIdChange = (index: number, value: string | null) => {
    const currentData = losMetadata || [];
    const newData = [...currentData];
    
    const isFromDropdown = losFieldMetadata.some(f => f.fieldName === value);
    
    if (isFromDropdown) {
      newData[index] = { ...newData[index], fieldName: value, losFieldId: '', value: '' };
    } else {
      newData[index] = { ...newData[index], losFieldId: value || '', fieldName: null, value: '' };
    }
    
    setValue('losMetadata', newData, { shouldValidate: true });
  };

  const handleLosMetadataValueChange = (index: number, value: string) => {
    const currentData = losMetadata || [];
    const newData = [...currentData];
    newData[index] = { ...newData[index], value };
    setValue('losMetadata', newData, { shouldValidate: true });
  };

  const getLosFieldDataType = (item: FeeLosMetadataConfigurationViewModel): DataType | null => {
    const field = getLosFieldMetadata(item);
    return field?.dataType || null;
  };

  const isLosFieldCurrency = (item: FeeLosMetadataConfigurationViewModel): boolean => {
    const searchValue = item.fieldName || item.losFieldId;
    return searchValue?.toLowerCase().includes('amount') || false;
  };

  const isLosFieldPercentage = (item: FeeLosMetadataConfigurationViewModel): boolean => {
    const searchValue = item.fieldName || item.losFieldId;
    return searchValue?.toLowerCase().includes('percentage') || false;
  };

  const shouldShowLosDropdown = (item: FeeLosMetadataConfigurationViewModel): boolean => {
    const field = getLosFieldMetadata(item);
    if (!field) return false;
    return field.dataType === DataType.TrueFalse || 
           (field.dataType === DataType.Enumeration && !!field.enumType);
  };

  const getLosValueOptions = (item: FeeLosMetadataConfigurationViewModel) => {
    const field = getLosFieldMetadata(item);
    if (!field) return [];

    if (field.dataType === DataType.TrueFalse) {
      return [
        { label: 'True', value: 'True' },
        { label: 'False', value: 'False' }
      ];
    }

    if (field.dataType === DataType.Enumeration && field.enumType) {
      return getEnumValuesForField(field).map(ev => ({
        label: ev.displayName || ev.name || '',
        value: ev.value
      }));
    }

    return [];
  };

  const isLoadingEnumForLosField = (item: FeeLosMetadataConfigurationViewModel): boolean => {
    const field = getLosFieldMetadata(item);
    if (!field || !field.enumType) return false;
    return loadingEnums[field.enumType] || false;
  };

  const fetchEnumValues = async (enumTypeName: string) => {
    if (enumValuesCache[enumTypeName]) {
      return enumValuesCache[enumTypeName];
    }

    if (fetchingEnumsRef.current.has(enumTypeName)) {
      return [];
    }

    fetchingEnumsRef.current.add(enumTypeName);
    setLoadingEnums(prev => ({ ...prev, [enumTypeName]: true }));
    
    try {
      const enumValues = await commonService.getEnumValues(enumTypeName);
      setEnumValuesCache(prev => ({ ...prev, [enumTypeName]: enumValues }));
      return enumValues;
    } catch (error) {
      console.error(`Failed to fetch enum values for ${enumTypeName}:`, error);
      return [];
    } finally {
      fetchingEnumsRef.current.delete(enumTypeName);
      setLoadingEnums(prev => ({ ...prev, [enumTypeName]: false }));
    }
  };

  const getEnumValuesForField = (fieldMetadata: FieldMetadata | null) => {
    if (!fieldMetadata || fieldMetadata.dataType !== DataType.Enumeration || !fieldMetadata.enumType) {
      return [];
    }

    if (enumValuesCache[fieldMetadata.enumType]) {
      return enumValuesCache[fieldMetadata.enumType];
    }

    if (!fetchingEnumsRef.current.has(fieldMetadata.enumType)) {
      fetchEnumValues(fieldMetadata.enumType);
    }

    return [];
  };


  useEffect(() => {
    if (visible) {
      if (hudLine) {
        console.log('Loading HUD Line for editing:', hudLine);
        console.log('LOS Metadata from API:', hudLine.losMetadata);
        reset({
          hudLine: hudLine.hudLine || '',
          description: hudLine.description || '',
          hudSection: hudLine.hudSection || '',
          loanEstimateSection: hudLine.loanEstimateSection || '',
          closingDisclosureSection: hudLine.closingDisclosureSection || '',
          group: hudLine.group || '',
          groupDetails: hudLine.groupDetails || '',
          isAprAffiliate: hudLine.isAprAffiliate,
          isAprNonAffiliate: hudLine.isAprNonAffiliate,
          estimateIncludedInEscrow: hudLine.estimateIncludedInEscrow,
          borrowerCanShopFor: hudLine.borrowerCanShopFor,
          paidTo: hudLine.paidTo || PaidTo.None,
          losVendor: hudLine.losVendor || IntegrationVendor.None,
          restrictions: hudLine.restrictions ? {
            ...hudLine.restrictions,
            toleranceLevel: hudLine.restrictions.toleranceLevel || FeeToleranceLevel.None,
          } : {
            toleranceLevel: FeeToleranceLevel.None,
            canEditBorrowerAmount: false,
            canEditSellerAmount: false,
            canEditBrokerAmount: false,
            canEditLenderAmount: false,
            canEditIncrementDollarAmount: false,
            canEditOtherAmount: false,
            canClientIgnore: false,
          },
          losMetadata: hudLine.losMetadata || [],
        });
      } else {
        reset({
          hudLine: '',
          description: '',
          hudSection: '',
          loanEstimateSection: '',
          closingDisclosureSection: '',
          group: '',
          groupDetails: '',
          isAprAffiliate: false,
          isAprNonAffiliate: false,
          estimateIncludedInEscrow: false,
          borrowerCanShopFor: false,
          paidTo: PaidTo.None,
          losVendor: IntegrationVendor.None,
          restrictions: {
            toleranceLevel: FeeToleranceLevel.None,
            canEditBorrowerAmount: false,
            canEditSellerAmount: false,
            canEditBrokerAmount: false,
            canEditLenderAmount: false,
            canEditIncrementDollarAmount: false,
            canEditOtherAmount: false,
            canClientIgnore: false,
          },
          losMetadata: [],
        });
      }
    }
  }, [visible, hudLine, reset]);

  const onSubmit = async (data: CreateOrUpdateFeeHudLineViewModel) => {
    await onSave(data);
  };

  const paidToOptions = [
    { label: 'None', value: PaidTo.None },
    { label: 'Broker', value: PaidTo.Broker },
    { label: 'Lender', value: PaidTo.Lender },
    { label: 'Seller', value: PaidTo.Seller },
    { label: 'Investor', value: PaidTo.Investor },
    { label: 'Affiliate', value: PaidTo.Affiliate },
    { label: 'Other', value: PaidTo.Other },
  ];

  const losVendorOptions = [
    { label: 'None', value: IntegrationVendor.None },
    { label: 'Factual', value: IntegrationVendor.Factual },
    { label: 'Xactus', value: IntegrationVendor.Xactus },
    { label: 'Informative Research', value: IntegrationVendor.InformativeResearch },
    { label: 'MeridianLink', value: IntegrationVendor.MeridianLink },
    { label: 'Freddie Mac', value: IntegrationVendor.FreddieMac },
    { label: 'Fannie Mae', value: IntegrationVendor.FannieMae },
    { label: 'RatesBoard', value: IntegrationVendor.RatesBoard },
    { label: 'Flueid', value: IntegrationVendor.Flueid },
    { label: 'Vesta', value: IntegrationVendor.Vesta },
    { label: 'Resend', value: IntegrationVendor.Resend },
    { label: 'Snap', value: IntegrationVendor.Snap },
    { label: 'Truv', value: IntegrationVendor.Truv },
    { label: 'Equifax', value: IntegrationVendor.Equifax },
    { label: 'Truework', value: IntegrationVendor.Truework },
    { label: 'Argyle', value: IntegrationVendor.Argyle },
    { label: 'Ernst', value: IntegrationVendor.Ernst },
    { label: 'SmartFees', value: IntegrationVendor.SmartFees },
    { label: 'Empower', value: IntegrationVendor.Empower },
    { label: 'Encompass', value: IntegrationVendor.Encompass },
    { label: 'Experian', value: IntegrationVendor.Experian },
  ];

  const dialogFooter = (
    <div className="flex gap-2 justify-content-end">
      <Button 
        type="button"
        label="Cancel" 
        onClick={onCancel} 
        className="p-button-text" 
        disabled={isSubmitting} 
      />
      <Button 
        type="button"
        label="Save" 
        onClick={handleSubmit(onSubmit)} 
        disabled={isSubmitting} 
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      style={{ width: '900px', maxHeight: '90vh' }}
      header={hudLine ? 'Edit HUD Line' : 'Create HUD Line'}
      modal
      className="p-fluid"
      footer={dialogFooter}
      onHide={onCancel}
      contentStyle={{ overflowY: 'auto' }}
    >
      <form onSubmit={(e) => e.preventDefault()} className="flex flex-column gap-3">
        <FormInputText
          field="hudLine"
          control={control}
          label="HUD Line"
          errors={errors}
          placeholder="e.g., 801"
          inputClassName="border-1 border-solid border-gray-300 w-full"
          required
        />

        <FormInputText
          field="description"
          control={control}
          label="Description"
          errors={errors}
          placeholder="e.g., Origination Charges"
          inputClassName="border-1 border-solid border-gray-300 w-full"
          required
        />

        <FormInputText
          field="hudSection"
          control={control}
          label="HUD Section"
          errors={errors}
          placeholder="e.g., A"
          inputClassName="border-1 border-solid border-gray-300 w-full"
          required
        />

        <FormInputText
          field="loanEstimateSection"
          control={control}
          label="Loan Estimate Section"
          errors={errors}
          placeholder="e.g., Section A"
          inputClassName="border-1 border-solid border-gray-300 w-full"
          required
        />

        <FormInputText
          field="closingDisclosureSection"
          control={control}
          label="Closing Disclosure Section"
          errors={errors}
          placeholder="e.g., Section A"
          inputClassName="border-1 border-solid border-gray-300 w-full"
          required
        />

        <FormInputText
          field="group"
          control={control}
          label="Group"
          errors={errors}
          placeholder="e.g., Origination Charges"
          inputClassName="border-1 border-solid border-gray-300 w-full"
        />

        <FormInputText
          field="groupDetails"
          control={control}
          label="Group Details"
          errors={errors}
          placeholder="Additional group information"
          inputClassName="border-1 border-solid border-gray-300 w-full"
        />

        <FormDropdown
          field="paidTo"
          control={control}
          label="Paid To"
          errors={errors}
          options={paidToOptions}
          placeholder="Select who pays"
          inputClassName="border-1 border-solid border-gray-300 w-full"
        />

        <FormDropdown
          field="losVendor"
          control={control}
          label="LOS Vendor"
          errors={errors}
          options={losVendorOptions}
          placeholder="Select LOS Vendor"
          inputClassName="border-1 border-solid border-gray-300 w-full"
        />

        <div className="flex flex-column gap-2 mt-3">
          <label className="font-semibold">
            LOS Metadata
          </label>

          {losMetadata && losMetadata.length > 0 && (
            <div className="flex flex-column gap-2">
              {losMetadata.map((metadata, index) => {
                const displayValue = metadata.fieldName || metadata.losFieldId || '';
                
                return (
                  <div key={index} className="p-fluid p-input-filled flex gap-2 text-sm w-full">
                    <div className="flex-1">
                      <Controller
                        name={`losMetadata.${index}` as any}
                        control={control}
                        render={({ field, fieldState }) => (
                          <Dropdown
                            value={displayValue}
                            options={losFieldMetadata.map(f => ({
                              label: f.fieldName || '',
                              value: f.fieldName || ''
                            }))}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select or type custom field ID"
                            onChange={(e) => handleLosFieldIdChange(index, e.value)}
                            filter
                            disabled={loadingFields}
                            editable
                            className="w-full"
                          />
                        )}
                      />
                    </div>

                    <div className="flex-1">
                      {shouldShowLosDropdown(metadata) ? (
                        <FormDropdown
                          control={control}
                          errors={errors}
                          field={`losMetadata.${index}.value`}
                          label=""
                          options={getLosValueOptions(metadata)}
                          optionLabel="label"
                          optionValue="value"
                          placeholder={isLoadingEnumForLosField(metadata) ? 'Loading...' : 'Select value'}
                          className="mb-0"
                          onChange={(e) => handleLosMetadataValueChange(index, e.value)}
                          disabled={isLoadingEnumForLosField(metadata)}
                          showFilter
                        />
                      ) : getLosFieldDataType(metadata) === DataType.Number || 
                         getLosFieldDataType(metadata) === DataType.Integer ? (
                        <FormInputNumber
                          control={control}
                          errors={errors}
                          field={`losMetadata.${index}.value`}
                          label=""
                          placeholder="Enter value"
                          className="mb-0"
                          prefix={isLosFieldCurrency(metadata) ? '$ ' : undefined}
                          suffix={isLosFieldPercentage(metadata) ? ' %' : undefined}
                        />
                      ) : getLosFieldDataType(metadata) === DataType.Date ||
                         getLosFieldDataType(metadata) === DataType.DateTime ? (
                        <FormCalendar
                          control={control}
                          errors={errors}
                          field={`losMetadata.${index}.value`}
                          label=""
                          placeholder="Select date"
                          className="mb-0"
                          showTime={getLosFieldDataType(metadata) === DataType.DateTime}
                        />
                      ) : (
                        <FormInputText
                          control={control}
                          errors={errors}
                          field={`losMetadata.${index}.value`}
                          label=""
                          placeholder="Enter value"
                          className="mb-0"
                          onChange={(e) => handleLosMetadataValueChange(index, e.target.value)}
                        />
                      )}
                    </div>

                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="p-button-text p-button-sm p-button-danger"
                      onClick={() => handleRemoveLosMetadata(index)}
                      style={{ minWidth: '32px', padding: '0.25rem' }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-2">
            <Button
              type="button"
              label="Add Property"
              icon="pi pi-plus"
              link
              onClick={handleAddLosMetadata}
              style={{ padding: '0.5rem', width: 'auto' }}
            />
          </div>
        </div>

        <div className="flex flex-column gap-2">
          <label className="font-semibold">Options</label>
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center gap-2">
              <Controller
                name="isAprAffiliate"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    inputId="isAprAffiliate"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              <label htmlFor="isAprAffiliate" className="cursor-pointer">
                Is APR Affiliate
              </label>
            </div>

            <div className="flex align-items-center gap-2">
              <Controller
                name="isAprNonAffiliate"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    inputId="isAprNonAffiliate"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              <label htmlFor="isAprNonAffiliate" className="cursor-pointer">
                Is APR Non-Affiliate
              </label>
            </div>

            <div className="flex align-items-center gap-2">
              <Controller
                name="estimateIncludedInEscrow"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    inputId="estimateIncludedInEscrow"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              <label htmlFor="estimateIncludedInEscrow" className="cursor-pointer">
                Estimate Included in Escrow
              </label>
            </div>

            <div className="flex align-items-center gap-2">
              <Controller
                name="borrowerCanShopFor"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    inputId="borrowerCanShopFor"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              <label htmlFor="borrowerCanShopFor" className="cursor-pointer">
                Borrower Can Shop For
              </label>
            </div>
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default HudLineEditorDialog;

